import type { QueryClient } from '@tanstack/react-query';
import { AppUserRole } from '../../../constants/appUserRole';
import { AUTH_EXPIRED_FLASH_MESSAGE_KEY, AUTH_SESSION_QUERY_KEY } from '../constants';
import type { SignInResponse } from '../types/auth';
import { writeStoredSession } from './authSessionStorage';

/** Recognized on the axios client so refresh / 401 handling does not clear the session. */
export const LOCAL_DEV_MOCK_ACCESS_TOKEN = 'ffl_local_dev_mock_access';

const LOCAL_DEV_MOCK_REFRESH_TOKEN = 'ffl_local_dev_mock_refresh';

/**
 * When `NEXT_PUBLIC_APP_ENV=local`, the auth page can sign in as super admin without
 * calling the backend (see `AuthPage` submit handler).
 */
export function isLocalDevAuthBypassEnabled(): boolean {
  return process.env.NEXT_PUBLIC_APP_ENV === 'local';
}

export function isLocalDevMockSession(
  session: SignInResponse | null | undefined,
): boolean {
  return session?.data?.accessToken === LOCAL_DEV_MOCK_ACCESS_TOKEN;
}

export function buildLocalDevSuperAdminSession(): SignInResponse {
  const expiresIn = 60 * 60 * 24 * 365;
  return {
    success: true,
    data: {
      accessToken: LOCAL_DEV_MOCK_ACCESS_TOKEN,
      refreshToken: LOCAL_DEV_MOCK_REFRESH_TOKEN,
      idToken: 'ffl_local_dev_mock_id',
      expiresIn,
      tokenType: 'Bearer',
      accessTokenExpiresAt: Date.now() + expiresIn * 1000,
      user: {
        userId: 'local-dev-super-admin',
        email: 'local-dev@example.invalid',
        firstName: 'Local',
        lastName: 'SuperAdmin',
        role: AppUserRole.SUPER_ADMIN,
        status: 'active',
      },
      role: AppUserRole.SUPER_ADMIN,
    },
  };
}

/** Persists mock session and syncs React Query (call from client components). */
export function applyLocalDevSuperAdminSession(queryClient: QueryClient): void {
  const session = buildLocalDevSuperAdminSession();
  try {
    sessionStorage.removeItem(AUTH_EXPIRED_FLASH_MESSAGE_KEY);
  } catch {
    // ignore
  }
  queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, session);
  writeStoredSession(session);
}
